import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Download, Upload, FileSpreadsheet, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import {
  GUEST_CSV_TEMPLATE_FILENAME,
  MAX_GUEST_IMPORT_ROWS,
  ParsedGuestError,
  ParsedGuestRow,
  buildGuestCsv,
  downloadCsv,
  parseGuestCsv,
} from '@/lib/guestCsv';
import { Guest } from '@/types';

interface ImportGuestsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called once the user clicks "Importar" with the validated rows. The page
   * is responsible for persisting them (and showing toasts on success/error).
   * `onProgress` is invoked as each batch lands so the modal can render a
   * determinate progress bar.
   */
  onImport: (
    guests: Array<Omit<Guest, 'id' | 'event_id'>>,
    onProgress: (inserted: number, total: number) => void,
  ) => Promise<void>;
}

export const ImportGuestsModal: React.FC<ImportGuestsModalProps> = ({
  open,
  onOpenChange,
  onImport,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedGuestRow[]>([]);
  const [errors, setErrors] = useState<ParsedGuestError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ inserted: number; total: number } | null>(null);

  const exceedsCap = parsed.length > MAX_GUEST_IMPORT_ROWS;

  const reset = () => {
    setFileName(null);
    setParsed([]);
    setErrors([]);
    setParseError(null);
    setSubmitting(false);
    setProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (next: boolean) => {
    // Don't let the user dismiss mid-import; the bulk insert is already running.
    if (!next && submitting) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleDownloadTemplate = () => {
    downloadCsv(GUEST_CSV_TEMPLATE_FILENAME, buildGuestCsv([]));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setFileName(file.name);

    try {
      const text = await file.text();
      const result = parseGuestCsv(text);
      setParsed(result.guests);
      setErrors(result.errors);
    } catch (err) {
      console.error('Error reading CSV file:', err);
      setParseError('No se pudo leer el archivo. Asegúrate de que sea un CSV válido.');
      setParsed([]);
      setErrors([]);
    }
  };

  const handleSubmit = async () => {
    if (parsed.length === 0 || exceedsCap) return;
    try {
      setSubmitting(true);
      setProgress({ inserted: 0, total: parsed.length });
      await onImport(
        parsed.map((g) => ({
          nombre: g.nombre,
          tel: g.tel,
          estado: g.estado,
          fecha_respuesta: g.estado !== 'PENDIENTE' ? new Date() : undefined,
          numero_acompanantes: 0,
          asistencia: 'PENDIENTE' as const,
        })),
        (inserted, total) => setProgress({ inserted, total }),
      );
      reset();
      onOpenChange(false);
    } catch (err) {
      // Page-level toast handles the user-visible error; just stop the spinner
      // and leave the progress bar at its last position so the user sees how
      // far the import got before failing.
      console.error('Error importing guests:', err);
      setSubmitting(false);
    }
  };

  const triggerFilePicker = () => fileInputRef.current?.click();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importar invitados desde CSV</DialogTitle>
          <DialogDescription>
            Descarga la plantilla, llénala con tu lista de invitados y súbela. Las columnas
            obligatorias son <strong>Nombre</strong> y <strong>Teléfono</strong>. La columna{' '}
            <strong>Estado</strong> es opcional (por defecto será PENDIENTE).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleDownloadTemplate} className="flex-1">
              <Download className="w-4 h-4 mr-2" />
              Descargar plantilla CSV
            </Button>
            <Button onClick={triggerFilePicker} variant="outline" className="flex-1">
              <Upload className="w-4 h-4 mr-2" />
              {fileName ? 'Elegir otro archivo' : 'Seleccionar archivo CSV'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {fileName && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FileSpreadsheet className="w-4 h-4" />
              <span className="truncate">{fileName}</span>
            </div>
          )}

          {parseError && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertTitle>No se pudo procesar el archivo</AlertTitle>
              <AlertDescription>{parseError}</AlertDescription>
            </Alert>
          )}

          {exceedsCap && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertTitle>Demasiados invitados en un solo archivo</AlertTitle>
              <AlertDescription>
                Solo se pueden importar hasta {MAX_GUEST_IMPORT_ROWS} invitados por archivo.
                Tu archivo tiene {parsed.length}. Divide la lista en varios archivos e
                impórtalos uno por uno.
              </AlertDescription>
            </Alert>
          )}

          {fileName && !parseError && (
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md border p-3 bg-emerald-50 border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Listos para importar</span>
                </div>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{parsed.length}</p>
              </div>
              <div className="rounded-md border p-3 bg-red-50 border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Filas con error</span>
                </div>
                <p className="text-2xl font-bold text-red-700 mt-1">{errors.length}</p>
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertTitle>Se omitirán {errors.length} fila(s) con errores</AlertTitle>
              <AlertDescription>
                <ScrollArea className="max-h-32 mt-2">
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    {errors.map((err, idx) => (
                      <li key={idx}>
                        Fila {err.rowNumber}: {err.message}
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </AlertDescription>
            </Alert>
          )}

          {parsed.length > 0 && (
            <div className="rounded-md border">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-700">
                Vista previa
              </div>
              <ScrollArea className="max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-left text-gray-600">
                      <th className="px-3 py-2 font-medium">Nombre</th>
                      <th className="px-3 py-2 font-medium">Teléfono</th>
                      <th className="px-3 py-2 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.slice(0, 50).map((g) => (
                      <tr key={g.rowNumber} className="border-t">
                        <td className="px-3 py-2">{g.nombre}</td>
                        <td className="px-3 py-2">{g.tel}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline">{g.estado}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
              {parsed.length > 50 && (
                <div className="px-3 py-2 text-xs text-gray-500 border-t bg-gray-50">
                  Mostrando los primeros 50 de {parsed.length} invitados.
                </div>
              )}
            </div>
          )}
        </div>

        {submitting && progress && (
          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Importando {progress.inserted} de {progress.total}…
              </span>
              <span className="text-gray-500 tabular-nums">
                {progress.total > 0
                  ? Math.round((progress.inserted / progress.total) * 100)
                  : 0}
                %
              </span>
            </div>
            <Progress
              value={progress.total > 0 ? (progress.inserted / progress.total) * 100 : 0}
            />
            <p className="text-xs text-gray-500">
              No cierres esta ventana hasta que termine la importación.
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={parsed.length === 0 || submitting || exceedsCap}
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Importar {parsed.length > 0 && !exceedsCap ? `(${parsed.length})` : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
